import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Printer, Phone, Globe, Volume2, VolumeX, ArrowLeft, Pencil, Loader2 } from "lucide-react";
import { AUTO_STAGES, URGENCY_STYLES, URGENCY_LABEL, fmtDateTime, timeInCurrentStage } from "@/lib/autoWorkflow";

const STATUS_FLOW = ["Pending", "Preparing", "Ready", "Completed"];
const STATUS_STYLES = {
  Pending: "bg-amber-100 text-amber-800 border-amber-200",
  Preparing: "bg-blue-100 text-blue-800 border-blue-200",
  Ready: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Completed: "bg-zinc-200 text-zinc-700 border-zinc-300",
};

// Appointment board only tracks the in-progress workflow — cancelled/no_show
// are terminal exceptions, not stages a live board moves things through.
const APPOINTMENT_FLOW = ["pending", "confirmed", "completed"];
const APPOINTMENT_STYLES = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-zinc-200 text-zinc-700 border-zinc-300",
};
const stageLabel = (tenant, statusKey) => {
  const raw = tenant?.status_labels?.[statusKey];
  return (raw?.label || "").trim() || statusKey;
};
const orderStageLabel = stageLabel;
const autoStageLabel = (tenant, statusKey) => tenant?.auto_status_labels?.[statusKey]?.label?.trim() || statusKey.replaceAll("_", " ");
const fmtApptTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};
const apiErrorMessage = (error, fallback = "Something went wrong") => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return fallback;
};
const datetimeLocalValue = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function Kitchen() {
  const { slug } = useParams();
  const [tenant, setTenant] = useState(null);
  const [orders, setOrders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [autoWorkOrders, setAutoWorkOrders] = useState([]);
  const [isServiceProvider, setIsServiceProvider] = useState(false);
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [chimeOn, setChimeOn] = useState(true);
  const lastIdsRef = useRef(new Set());

  const playChime = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.setValueAtTime(880, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.25);
      g.gain.setValueAtTime(0.18, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      o.start(); o.stop(ctx.currentTime + 0.4);
    } catch {}
  };

  const load = async () => {
    try {
      const t = await api.get(`/tenants/${slug}/admin`);
      setTenant(t.data);
      const serviceProvider = t.data.business_category === "service_provider";
      setIsServiceProvider(serviceProvider);

      if (t.data.vertical_key === "auto") {
        // Auto fulfillment is not optional (the backend rejects toggling
        // fulfillment_queue for vertical_key="auto" — it's managed entirely
        // by the auto operations queue), so no disabled-state check here.
        const workOrders = await api.get(`/tenants/${slug}/auto/work-orders`);
        setAutoWorkOrders(workOrders.data || []);
        setStatus("auto_ready");
        return;
      }

      if (serviceProvider) {
        if ((t.data.booking_mode || "off") === "off") {
          setAppointments([]);
          setMessage("Bookings are off for this business. Enable them in dashboard settings to use this board.");
          setStatus("unavailable");
          return;
        }
        if (t.data.features?.fulfillment_queue === false) {
          setAppointments([]);
          setMessage("Fulfillment queue is disabled for this business. Re-enable it in dashboard settings to use this board.");
          setStatus("unavailable");
          return;
        }
        const a = await api.get(`/tenants/${slug}/appointments`);
        const newAppointments = a.data;
        const currentIds = new Set(newAppointments.map(x => x.id));
        if (lastIdsRef.current.size > 0) {
          const fresh = [...currentIds].filter(id => !lastIdsRef.current.has(id));
          if (fresh.length > 0 && chimeOn) playChime();
        }
        lastIdsRef.current = currentIds;
        setAppointments(newAppointments);
        setStatus("ready");
        return;
      }

      if (t.data.features?.fulfillment_queue === false) {
        setOrders([]);
        setMessage("Fulfillment queue is disabled for this business. Re-enable it in dashboard settings to use this board.");
        setStatus("unavailable");
        return;
      }

      const o = await api.get(`/tenants/${slug}/orders`, { params: { only_paid: true } });
      const newOrders = o.data;
      // Detect new arrivals for chime
      const currentIds = new Set(newOrders.map(x => x.id));
      if (lastIdsRef.current.size > 0) {
        const fresh = [...currentIds].filter(id => !lastIdsRef.current.has(id));
        if (fresh.length > 0 && chimeOn) playChime();
      }
      lastIdsRef.current = currentIds;
      setOrders(newOrders);
      setStatus("ready");
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Couldn't load fulfillment queue.");
      setStatus("error");
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [slug, chimeOn]);

  const advance = async (o) => {
    const next = STATUS_FLOW[STATUS_FLOW.indexOf(o.order_status) + 1];
    if (!next) return;
    try {
      await api.patch(`/orders/${o.id}/status`, { order_status: next });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't move order");
    }
  };

  const moveToStage = async (event, stage) => {
    event.preventDefault();
    const orderId = event.dataTransfer.getData("text/order-id");
    const order = orders.find(item => item.id === orderId);
    if (!order || order.order_status === stage) return;
    try {
      await api.patch(`/orders/${orderId}/status`, { order_status: stage });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't move order");
    }
  };

  const reprint = async (o) => {
    try {
      const result = await api.post(`/orders/${o.id}/reprint`);
      toast.success(result.data?.printed ? "Ticket sent to printer" : "No printer is configured");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reprint ticket");
    }
  };

  const advanceAppointment = async (a) => {
    const next = APPOINTMENT_FLOW[APPOINTMENT_FLOW.indexOf(a.status) + 1];
    if (!next) return;
    try {
      await api.patch(`/tenants/${slug}/appointments/${a.id}`, { status: next });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't move appointment");
    }
  };

  const moveAppointmentToStage = async (event, stage) => {
    event.preventDefault();
    const appointmentId = event.dataTransfer.getData("text/appointment-id");
    const appointment = appointments.find(item => item.id === appointmentId);
    if (!appointment || appointment.status === stage) return;
    try {
      await api.patch(`/tenants/${slug}/appointments/${appointmentId}`, { status: stage });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't move appointment");
    }
  };

  const advanceAuto = async (workOrder, nextStatus) => {
    if (!nextStatus) return;
    try {
      await api.patch(`/tenants/${slug}/auto/work-orders/${workOrder.id}`, { status: nextStatus });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't move work order");
    }
  };

  const moveAutoToStage = (event, stage) => {
    event.preventDefault();
    const id = event.dataTransfer.getData("text/auto-work-order");
    const workOrder = autoWorkOrders.find((item) => item.id === id);
    if (workOrder && workOrder.status !== stage) advanceAuto(workOrder, stage);
  };

  if (status === "loading") return <div className="p-12">Loading fulfillment queue...</div>;
  if (status === "error" || status === "unavailable") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <Card className="p-8 max-w-md text-center">
          <h1 className="font-display text-2xl mb-2">{status === "error" ? "Can't load fulfillment" : "Fulfillment unavailable"}</h1>
          <p className="text-sm text-muted-foreground mb-5">{message}</p>
          <Link to={`/dashboard/${slug}`}><Button variant="outline"><ArrowLeft className="w-3.5 h-3.5 mr-1"/>Back to dashboard</Button></Link>
        </Card>
      </div>
    );
  }

  if (status === "auto_ready") {
    const stages = AUTO_STAGES.filter((stage) => stage !== "cancelled");
    return (
      <div className="min-h-screen bg-background">
        <div className="bg-zinc-900 text-white"><div className="max-w-[1800px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between flex-wrap gap-4"><div><p className="text-xs tracking-[0.2em] uppercase text-white/60">Auto fulfillment control room</p><h1 className="font-display text-2xl">{tenant.name}</h1></div><Link to={`/dashboard/${slug}`} className="text-xs text-white/70 hover:text-white flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5"/>Back to dashboard</Link></div></div>
        <div className="max-w-[1800px] mx-auto px-6 lg:px-10 py-6"><div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">{stages.map((stage) => <div key={stage} onDragOver={(event) => event.preventDefault()} onDrop={(event) => moveAutoToStage(event, stage)}><div className="flex items-center justify-between mb-3"><p className="overline">{autoStageLabel(tenant, stage)}</p><Badge variant="outline">{autoWorkOrders.filter((item) => item.status === stage).length}</Badge></div><div className="space-y-3">{autoWorkOrders.filter((item) => item.status === stage).map((item) => { const next = stages[stages.indexOf(stage) + 1]; return <Card key={item.id} draggable onDragStart={(event) => event.dataTransfer.setData("text/auto-work-order", item.id)} className="p-4 cursor-grab active:cursor-grabbing"><div className="flex items-start justify-between mb-2 gap-2"><div><div className="font-semibold">{item.title}</div><div className="text-xs text-muted-foreground">{item.customer_name} · {[item.year, item.make, item.model].filter(Boolean).join(" ")}</div></div><Badge variant="outline" className={`shrink-0 text-[10px] ${URGENCY_STYLES[item.urgency] || ""}`}>{URGENCY_LABEL[item.urgency] || item.urgency}</Badge></div><p className="text-sm mb-2">{item.concern || "No concern recorded"}</p><div className="flex items-center justify-between text-[11px] text-muted-foreground font-mono mb-3"><span>In queue {fmtDateTime(item.stage_history?.[0]?.entered_at || item.created_at)}</span><span>{timeInCurrentStage(item)} in stage</span></div>{next ? <Button size="sm" variant="outline" className="w-full" onClick={() => advanceAuto(item, next)}>Move to {autoStageLabel(tenant, next)}</Button> : null}</Card>; })}</div></div>)}</div></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-zinc-900 text-white">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-white/60">Fulfillment Control Room</p>
            <h1 className="font-display text-2xl">{tenant.name}</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to={`/dashboard/${slug}`} className="text-xs text-white/70 hover:text-white flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5"/>Back to dashboard</Link>
            <div className="flex items-center gap-2 text-sm">
              {chimeOn ? <Volume2 className="w-4 h-4"/> : <VolumeX className="w-4 h-4 text-white/50"/>}
              <span className="text-white/80">Chime</span>
              <Switch checked={chimeOn} onCheckedChange={setChimeOn} data-testid="kitchen-chime-toggle"/>
            </div>
            <div className="text-xs text-white/60 font-mono">auto-refresh · 4s</div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-6">
        {isServiceProvider ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {APPOINTMENT_FLOW.map(s => (
              <div key={s} onDragOver={event => event.preventDefault()} onDrop={event => moveAppointmentToStage(event, s)}>
                <div className="flex items-center justify-between mb-3">
                  <p className="overline">{stageLabel(tenant, s)}</p>
                  <Badge variant="outline">{appointments.filter(a => a.status === s).length}</Badge>
                </div>
                <div className="space-y-3">
                  {appointments.filter(a => a.status === s).map(a => (
                    <Card key={a.id} draggable onDragStart={event => event.dataTransfer.setData("text/appointment-id", a.id)} className="p-4 cursor-grab active:cursor-grabbing" data-testid={`fulfillment-appt-${a.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-display text-lg leading-none">{a.appointment_type_name || "Booking"}</div>
                        <div className="text-xs text-muted-foreground font-mono">{fmtApptTime(a.requested_datetime)}</div>
                      </div>
                      <div className="text-sm font-medium">{a.customer_name}</div>
                      <div className="text-xs text-muted-foreground font-mono mb-3">{a.customer_phone}{a.guest_count > 1 ? ` · ${a.guest_count} guests` : ""}</div>
                      {a.notes && <div className="text-xs italic text-muted-foreground mb-2">"{a.notes}"</div>}
                      <FulfillmentAppointmentEditor slug={slug} appointment={a} onSaved={load} />
                      {s !== "completed" && (
                        <Button size="sm" onClick={() => advanceAppointment(a)} className="w-full" data-testid={`kitchen-appt-advance-${a.id}`}>
                          → {stageLabel(tenant, APPOINTMENT_FLOW[APPOINTMENT_FLOW.indexOf(s) + 1])}
                        </Button>
                      )}
                    </Card>
                  ))}
                  {appointments.filter(a => a.status === s).length === 0 && (
                    <div className={`rounded-lg border border-dashed border-border p-6 text-center text-xs text-muted-foreground ${APPOINTMENT_STYLES[s]} opacity-30`}>
                      Nothing here
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {STATUS_FLOW.map(s => (
              <div key={s} onDragOver={event => event.preventDefault()} onDrop={event => moveToStage(event, s)}>
                <div className="flex items-center justify-between mb-3">
                  <p className="overline">{orderStageLabel(tenant, s)}</p>
                  <Badge variant="outline">{orders.filter(o => o.order_status === s).length}</Badge>
                </div>
                <div className="space-y-3">
                  {orders.filter(o => o.order_status === s).map(o => (
                    <Card key={o.id} draggable onDragStart={event => event.dataTransfer.setData("text/order-id", o.id)} className="p-4 cursor-grab active:cursor-grabbing" data-testid={`fulfillment-order-${o.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-display text-2xl leading-none">#{o.order_number}</div>
                          <div className="text-xs text-muted-foreground font-mono">{new Date(o.timestamp).toLocaleTimeString()}</div>
                        </div>
                        <Badge className={`${o.source_type === "Phone Call" ? "bg-purple-100 text-purple-800 border-purple-200" : "bg-sky-100 text-sky-800 border-sky-200"} border`}>
                          {o.source_type === "Phone Call" ? <><Phone className="w-3 h-3 mr-1"/>Phone</> : o.source_type === "Staff" ? "Staff" : <><Globe className="w-3 h-3 mr-1"/>Web</>}
                        </Badge>
                      </div>
                      <div className="text-sm font-medium">{o.customer_name}</div>
                      <div className="text-xs text-muted-foreground font-mono mb-3">{o.phone_number}</div>
                      <ul className="space-y-0.5 text-sm mb-3">
                        {o.items.map((it, idx) => (
                          <li key={idx} className="flex justify-between"><span>{it.quantity}× {it.name}</span></li>
                        ))}
                      </ul>
                      <div className="flex justify-between items-center text-xs font-mono text-muted-foreground mb-3">
                        <span>{o.payment_status === "PayAtPickup" ? "Pay at pickup" : o.payment_status}</span>
                        <span className="text-foreground">${o.total_price.toFixed(2)}</span>
                      </div>
                      <StageTimer order={o}/>
                      {o.notes && <div className="text-xs italic text-muted-foreground mb-2">"{o.notes}"</div>}
                      <div className="flex gap-2">
                        {o.order_status !== "Completed" && (
                          <Button size="sm" onClick={() => advance(o)} className="flex-1" data-testid={`kitchen-advance-${o.id}`}>
                            → {orderStageLabel(tenant, STATUS_FLOW[STATUS_FLOW.indexOf(o.order_status)+1])}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => reprint(o)} data-testid={`kitchen-reprint-${o.id}`}>
                          <Printer className="w-3.5 h-3.5"/>
                        </Button>
                      </div>
                    </Card>
                  ))}
                  {orders.filter(o => o.order_status === s).length === 0 && (
                    <div className={`rounded-lg border border-dashed border-border p-6 text-center text-xs text-muted-foreground ${STATUS_STYLES[s]} opacity-30`}>
                      No orders
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FulfillmentAppointmentEditor({ slug, appointment, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_name: appointment.customer_name || "",
    customer_phone: appointment.customer_phone || "",
    customer_email: appointment.customer_email || "",
    appointment_type_name: appointment.appointment_type_name || "",
    requested_datetime: datetimeLocalValue(appointment.requested_datetime),
    guest_count: appointment.guest_count || 1,
    notes: appointment.notes || "",
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      customer_name: appointment.customer_name || "",
      customer_phone: appointment.customer_phone || "",
      customer_email: appointment.customer_email || "",
      appointment_type_name: appointment.appointment_type_name || "",
      requested_datetime: datetimeLocalValue(appointment.requested_datetime),
      guest_count: appointment.guest_count || 1,
      notes: appointment.notes || "",
    });
  }, [open, appointment]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const save = async () => {
    if (!form.customer_name.trim()) return toast.error("Client name is required");
    if (!form.appointment_type_name.trim()) return toast.error("Service name is required");
    if (!form.requested_datetime) return toast.error("Date and time are required");
    setSaving(true);
    try {
      await api.patch(`/tenants/${slug}/appointments/${appointment.id}`, {
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim() || undefined,
        customer_email: form.customer_email.trim() || undefined,
        appointment_type_name: form.appointment_type_name.trim(),
        requested_datetime: `${form.requested_datetime}:00`,
        guest_count: Number(form.guest_count) || 1,
        notes: form.notes.trim() || null,
      });
      toast.success("Booking card updated");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't update booking card"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="w-full mb-2" onClick={(event) => event.stopPropagation()}>
          <Pencil className="w-3.5 h-3.5 mr-1"/>Edit card
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit fulfillment card</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold mb-1 block">Client name</label>
            <Input value={form.customer_name} onChange={event => update("customer_name", event.target.value)} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold mb-1 block">Phone</label>
              <Input value={form.customer_phone} onChange={event => update("customer_phone", event.target.value)} />
            </div>
            <div>
              <label className="text-xs font-bold mb-1 block">Email</label>
              <Input type="email" value={form.customer_email} onChange={event => update("customer_email", event.target.value)} />
            </div>
          </div>
          <div className="grid sm:grid-cols-[1fr_120px] gap-3">
            <div>
              <label className="text-xs font-bold mb-1 block">Service</label>
              <Input value={form.appointment_type_name} onChange={event => update("appointment_type_name", event.target.value)} />
            </div>
            <div>
              <label className="text-xs font-bold mb-1 block">Guests</label>
              <Input type="number" min="1" max="50" value={form.guest_count} onChange={event => update("guest_count", event.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold mb-1 block">Date and time</label>
            <Input type="datetime-local" value={form.requested_datetime} onChange={event => update("requested_datetime", event.target.value)} />
          </div>
          <div>
            <label className="text-xs font-bold mb-1 block">Notes</label>
            <Textarea className="min-h-[96px]" value={form.notes} onChange={event => update("notes", event.target.value)} />
          </div>
          <Button className="w-full" onClick={save} disabled={saving}>
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Saving...</> : "Save card"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StageTimer({ order }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const started = new Date(order.stage_started_at || order.timestamp).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - started) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [order.stage_started_at, order.timestamp]);
  return <div className="text-[11px] text-muted-foreground font-mono mb-3">Stage time · {Math.floor(elapsed / 60)}m {String(elapsed % 60).padStart(2, "0")}s</div>;
}
